import type { DownloadInfo } from '@bludata/fc-storage';
import { BusinessError } from '@bludata/common-ui';
import { getStorageClient } from '@focus.cloud/src/login/api/ClientServicesCloud';

interface DownloadWithRetryOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
  signal?: AbortSignal;
  onAttempt?: (attempt: number, nextDelayMs: number) => void;
}

const DEFAULT_MAX_ATTEMPTS = 5;
const DEFAULT_BASE_DELAY_MS = 500;
const DEFAULT_MAX_DELAY_MS = 8000;

const safeStringify = (val: unknown): string => {
  try {
    return JSON.stringify(val);
  } catch {
    return '';
  }
};

// Riconosce SOLO il caso "file non ancora disponibile" emesso dallo storage
// (eventual consistency post-upload / job asincrono in corso).
//
// Caso reale: ClientBase di @bludata/common-ui intercetta il 404 con
// `isFaulted: true` e `exceptionType: 'ObjectNotFoundException'`, lo wrappa
// in un BusinessError(subType='ObjectNotFound', message="Object of type
// 'StoredFile' with id '...' was not found"). La stringa
// 'ObjectNotFoundException' a runtime non è più presente, va matchato
// l'errore tipizzato.
//
// Fallback: chiamate dirette ad axios (non passate per ClientBase) che
// possano emergere come AxiosError raw.
const isStoredFileNotFound = (err: unknown): boolean => {
  if (!err || typeof err !== 'object') return false;

  // Caso 1 — BusinessError 'ObjectNotFound' su tipo StoredFile
  if (err instanceof BusinessError && err.subType === 'ObjectNotFound') {
    return (err.message ?? '').includes('StoredFile');
  }

  // Caso 2 — AxiosError raw (404 con body che contiene il pattern atteso)
  const e = err as {
    response?: { status?: number; data?: unknown };
    message?: string;
  };
  if (e.response?.status === 404) {
    const data = e.response.data;
    const dataString =
      typeof data === 'string' ? data : data ? safeStringify(data) : '';
    return (
      dataString.includes('ObjectNotFoundException') &&
      dataString.includes('StoredFile')
    );
  }

  return false;
};

// Backoff esponenziale con full jitter: random(0, min(max, base * 2^attempt))
const computeBackoff = (
  attempt: number,
  baseMs: number,
  maxMs: number
): number => {
  const exp = baseMs * Math.pow(2, attempt);
  const capped = Math.min(maxMs, exp);
  return Math.random() * capped;
};

// Sleep cancellabile via AbortSignal (S6 HARD in focus.cloud).
// Listener registrato PRIMA del setTimeout per chiudere la micro-finestra
// in cui un abort sincrono potrebbe arrivare tra le due righe.
const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onAbort = () => {
      if (timer !== undefined) clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
  });

/**
 * Esegue `getStorageClient().download(uri)` con retry esponenziale silenzioso
 * sul SOLO errore "file non ancora disponibile" (BusinessError ObjectNotFound
 * di tipo StoredFile, o 404 raw con il pattern equivalente). Tutti gli altri
 * errori vengono propagati subito.
 *
 * Default: 5 tentativi, base 500ms, cap 8s, full jitter — wall time worst case
 * ~15.5s, mediano ~8s.
 */
export const downloadWithRetry = async (
  uri: string,
  options?: DownloadWithRetryOptions
): Promise<DownloadInfo> => {
  const maxAttempts = Math.max(
    1,
    options?.maxAttempts ?? DEFAULT_MAX_ATTEMPTS
  );
  const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
  const maxDelayMs = options?.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (options?.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError');
    }
    try {
      return await getStorageClient().download(uri);
    } catch (err) {
      // Predicato preciso: se non è il pattern di eventual consistency
      // o se i tentativi sono esauriti, rilancia preservando stack/trace.
      if (!isStoredFileNotFound(err) || attempt === maxAttempts - 1) {
        throw err;
      }
      const delayMs = computeBackoff(attempt, baseDelayMs, maxDelayMs);
      options?.onAttempt?.(attempt + 1, delayMs);
      await sleep(delayMs, options?.signal);
    }
  }

  // Unreachable — il loop esce sempre via return o throw.
  throw new Error('downloadWithRetry: stato inatteso');
};
